import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  DatasourcesActions,
  datasourcesSubject,
  getNodeInfo,
  setCraftedComponents,
  setSharedSources,
  DesignerRequestVerb,
  INodeInfo,
  IComponentTemplate,
  OrNull,
} from '@qodly/shared';
import { defaultTo, isEmpty, isObject } from 'lodash';
import { ITab } from '@qodly/shared';
import {
  by,
  outById,
  getDatasourceId,
  isObjectDatasource,
  isSimpleDatasource,
} from '@qodly/shared';
import { AsyncThunkConfig } from '~store/hooks/store';
import {
  addComponent,
  editComponent,
  removeComponent,
  setTemplateData,
  setTemplatesData,
} from '.';
import { AppState } from '..';
import { listDsNamespaces } from './datasources.adapter';
import { ITemplatedGroup, ITemplatedGroupItem } from './types';

export const fetchTemplates = createAsyncThunk<
  ITemplatedGroup[],
  void,
  { state: AppState }
>('webforms/fetchTemplates', async (_, { dispatch, getState }) => {
  const { data } = getState().webforms.templates;
  const tipsBaseUrl = getState().settings.session['studio.tipsBaseUrl'] || '';

  if (!isEmpty(data)) return data;
  try {
    const res = await fetch(`${tipsBaseUrl}/build/index.json?t=${Date.now()}`);
    // const res = await fetch(`templates/index.json?t=${Date.now()}`);
    const data = (await res.json()) as ITemplatedGroup[];
    dispatch(setTemplatesData(data));
    return data;
  } catch (e) {
    return [];
  }
});

export const fetchTemplate = createAsyncThunk<
  ITemplatedGroupItem[],
  { name: string },
  { state: AppState }
>('webforms/fetchTemplate', async ({ name }, { dispatch, getState }) => {
  const { data } = getState().webforms.templates;
  const found = data.find((t) => t.group === name);
  if (found && !isEmpty(found.components)) return found.components;
  try {
    const res = await fetch(`${found?.link}?t=${Date.now()}`);
    const data = (await res.json()) as ITemplatedGroupItem[];
    dispatch(setTemplateData({ group: name, components: data }));
    return data;
  } catch (e) {
    return [];
  }
});

/* -------------------------------------------------------------------------- */
/*                             Crafted Components                             */
/* -------------------------------------------------------------------------- */

export const fetchCraftedComponents = createAsyncThunk(
  'webforms/fetchCraftedComponents',
  async (_, { rejectWithValue }) => {
    try {
      const crafts = await getNodeInfo({
        path: 'WebForms/crafted_components.json',
        verb: DesignerRequestVerb.GET_INFO,
        content: true,
        as: 'json',
      });

      return (crafts as INodeInfo).content;
    } catch (e) {
      return rejectWithValue('Error while loading the crafted components');
    }
  },
);

export const addCraftedComponent = createAsyncThunk(
  'webforms/addCraftedComponent',
  async (
    payload: IComponentTemplate,
    { dispatch, rejectWithValue, getState },
  ) => {
    try {
      dispatch(addComponent(payload));
      const {
        webforms: { components },
      } = getState() as AppState;
      setCraftedComponents(components);
    } catch (e) {
      return rejectWithValue('Error while adding the crafted component');
    }
  },
);

export const editCraftedComponent = createAsyncThunk(
  'webforms/editCraftedComponent',
  async (
    payload: {
      id: string;
      changes: Partial<IComponentTemplate>;
    },
    { dispatch, rejectWithValue, getState },
  ) => {
    try {
      dispatch(editComponent(payload));
      const {
        webforms: { components },
      } = getState() as AppState;
      setCraftedComponents(components);
    } catch (e) {
      return rejectWithValue('Error while editing the crafted component');
    }
  },
);

export const removeCraftedComponent = createAsyncThunk(
  'webforms/removeCraftedComponent',
  async (payload: string, { dispatch, rejectWithValue, getState }) => {
    try {
      dispatch(removeComponent(payload));
      const {
        webforms: { components },
      } = getState() as AppState;
      setCraftedComponents(components);
    } catch (e) {
      return rejectWithValue('Error while removing the crafted component');
    }
  },
);

/* -------------------------------------------------------------------------- */
/*                                 Datasources                                */
/* -------------------------------------------------------------------------- */

type TDatasourcesReturnType = {
  path: string;
  namespace: string;
  list: datasources.ICreateDataSource[];
};

type TAddDatasource = {
  datasource: datasources.ICreateDataSource;
  path: string;
};

export const addDatasource = createAsyncThunk<
  TDatasourcesReturnType | undefined,
  TAddDatasource,
  AsyncThunkConfig
>(
  'datasources/add-datasource',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState() as AppState;

      const datasource = payload.datasource;
      const is_shared = !!datasource.namespace;
      const list: datasources.ICreateDataSource[] = [];

      if (is_shared) {
        const shared_datasources = {
          ...state.sharedDatasources.sources,
          [datasource.namespace]: [
            ...state.sharedDatasources.sources[datasource.namespace],
            datasource,
          ],
        };
        list.push(...shared_datasources[datasource.namespace]);
        await setSharedSources(shared_datasources);
      } else {
        const tab = state.root.tabs.find(
          by('path', payload.path),
        ) as ITab<webforms.Content>;

        const datasources_list = tab.content?.metadata?.datasources || [];
        datasources_list.push(datasource);
      }
      return {
        list,
        path: payload.path,
        namespace: payload.datasource.namespace,
      };
    } catch (e) {
      console.error('Error while adding the datasource', payload.datasource.id);
      rejectWithValue('Error while adding the datasource');
    }
  },
);

type TEditDatasourceProperty = {
  id: string;
  path: string;
  value: any;
  property: string;
  namespace: string;
  updateRefs?: boolean;
};

type TEditDatasourceProperties = {
  id: string;
  path: string;
  changes: Record<string, any>;
  namespace: string;
  updateRefs?: boolean;
};

type TEditDatasource = TEditDatasourceProperty | TEditDatasourceProperties;

export const editDatasource = createAsyncThunk<
  TDatasourcesReturnType | undefined,
  TEditDatasource,
  AsyncThunkConfig
>(
  'datasources/edit-datasource',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const { namespace, path } = payload;
      const state = getState() as AppState;
      const is_shared = !!payload.namespace;
      const namespaces = listDsNamespaces(path, state);
      let list = defaultTo<datasources.ICreateDataSource[]>(
        namespaces[namespace || path],
        [],
      );
      const old_props = list.find(
        (ds) =>
          ds.id === payload.id &&
          (ds.namespace || '') === (payload.namespace || ''),
      );

      const changes = (payload as TEditDatasourceProperties).changes;
      const updates = changes ?? {
        [(payload as TEditDatasourceProperty).property]: (
          payload as TEditDatasourceProperty
        ).value,
      };

      list = list.map((ds) => {
        if (ds.id === payload.id)
          return {
            ...ds,
            ...updates,
          };
        return ds;
      });

      if (is_shared) {
        await setSharedSources({
          ...state.sharedDatasources.sources,
          [payload.namespace]: list,
        });
      }

      const changedKeys = isObject(changes) ? Object.keys(changes) : [];
      if (changedKeys.includes('dataType') && payload.updateRefs) {
        datasourcesSubject.next({
          datasource: {
            id: payload.id,
            namespace: payload.namespace,
          },
          path: path ?? '',
          action: DatasourcesActions.TYPE_CHANGE,
          old_type: old_props?.dataType || '',
          new_type: updates.dataType,
          is_shared,
        });
      }

      return {
        list,
        path: payload.path,
        namespace: payload.namespace,
      };
    } catch (error) {
      rejectWithValue(`Error while editing datasource ${payload.id}`);
    }
  },
);

export const isRenameDatasource = (
  action: PayloadAction<any>,
): action is PayloadAction<TRenameDatasource> =>
  action.type === renameDatasource.fulfilled.type;

type TRenameDatasource = {
  id: string;
  path: string;
  new_name: string;
  namespace: string;
  updateRefs: boolean;
};

export const renameDatasource = createAsyncThunk<
  OrNull<TDatasourcesReturnType>,
  TRenameDatasource,
  AsyncThunkConfig
>(
  'datasources/rename-datasource',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const is_shared = !!payload.namespace;
      const namespace = payload.namespace || payload.path;
      const namespaces = listDsNamespaces(payload.path, state);

      const list = defaultTo(namespaces[namespace] as webforms.Datasources, []);

      const regex = new RegExp(`^${payload.id}\\b`, '');

      list.forEach((ds) => {
        const isParent =
          (isObjectDatasource(ds) || !isSimpleDatasource(ds)) &&
          ds.from &&
          regex.test(ds.from);

        if (payload.updateRefs) {
          ds.events?.forEach((e) => {
            switch (e.type) {
              case 'method': {
                const event = e;
                event.params.forEach((param) => {
                  if (param.datasource) {
                    Object.assign(param, {
                      datasource: param.datasource.replace(
                        new RegExp(`^${payload.id}`, ''),
                        payload.new_name,
                      ),
                    });
                  }
                });
                if (event.returns) {
                  Object.assign(event.returns, {
                    datasource: event.returns?.datasource?.replace(
                      new RegExp(`^${payload.id}`, ''),
                      payload.new_name,
                    ),
                  });
                }
                return;
              }
              case 'simple-action': {
                const event = e;
                if (event.datasource) {
                  Object.assign(event.datasource, {
                    name: event.datasource.name?.replace(
                      new RegExp(`^${payload.id}`, ''),
                      payload.new_name,
                    ),
                    from: event.datasource.from?.replace(
                      new RegExp(`^${payload.id}`, ''),
                      payload.new_name,
                    ),
                    target: event.datasource.target?.replace(
                      new RegExp(`^${payload.id}`, ''),
                      payload.new_name,
                    ),
                  });
                }
                return;
              }
              default:
                return;
            }
          });
        }

        if (ds.id === payload.id) ds.id = payload.new_name;
        else if (isParent && ds.from)
          ds.from = ds.from.replace(
            new RegExp(`^${payload.id}`, ''),
            payload.new_name,
          );
      });

      if (payload.updateRefs) {
        // update references in the components
        const old_reference = getDatasourceId({
          id: payload.id,
          namespace: payload.namespace,
        });
        const new_reference = getDatasourceId({
          id: payload.new_name,
          namespace: payload.namespace,
        });
        datasourcesSubject.next({
          path: payload.path ?? '',
          action: DatasourcesActions.RENAME,
          old_reference,
          new_reference,
          is_shared,
        });
      }

      if (is_shared) {
        await setSharedSources({
          ...state.sharedDatasources.sources,
          [payload.namespace]: list,
        });
      }
      return {
        list,
        path: payload.path,
        namespace: payload.namespace,
      };
    } catch (error) {
      return rejectWithValue(
        `Error while renaming datasource ${payload.namespace}:${payload.id}`,
      );
    }
  },
);

type TDeleteDatasource = {
  path: string;
  id: string;
  namespace: string;
};

export const deleteDatasource = createAsyncThunk<
  OrNull<TDatasourcesReturnType>,
  TDeleteDatasource,
  AsyncThunkConfig
>(
  'datasources/delete-datasource',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState() as AppState;
      const is_shared = !!payload.namespace;
      const namespace = payload.namespace || payload.path;
      const namespaces = listDsNamespaces(payload.path, state);

      const filtered = namespaces[namespace].filter(outById(payload.id));

      if (is_shared) {
        await setSharedSources({
          ...state.sharedDatasources.sources,
          [payload.namespace]: filtered,
        });
      }
      return {
        list: filtered,
        path: payload.path,
        namespace: payload.namespace,
      };
    } catch (error) {
      return rejectWithValue(`Error while removing datasource ${payload.id}`);
    }
  },
);
